import React, { useEffect, useState } from 'react';
import { Modal } from 'react-responsive-modal';
import {
    Chip,
    Grid,
    IconButton,
    Tooltip,
    Typography,
    Box,
} from '@mui/material';
import { MasterMessage } from '../../../../masters/masterMessages';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ButtonGeneral from '../../../components/general/buttons/buttonGeneral';
import { SwalCustomClass } from '../../../components/general/toastSwal/swalCustomClass';
import {
    asyncDeleteAnswer,
    asyncGetAnswersByQuestion,
    asyncPatchAnswer,
    asyncPostAnswer,
    asyncUpdatePositionsAnwers,
} from '../../../../services/survey.service';
import FieldText from '../../../components/general/fields/fieldText';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import EditIcon from '@mui/icons-material/Edit';
import Loading from '../../../components/general/loading/loading';
import SaveIcon from '@mui/icons-material/Save';
import BlockIcon from '@mui/icons-material/Block';
import DraggableListView from '../../../components/views/draggableListView';

type Props = {
    open: boolean;
    onClose: (value: boolean) => void;
    saveLoad: (value: boolean) => void;
    idSurvey?: string;
    idQuestion?: string;
    type?: string;
};

const ModalAnswers = ({
    open,
    onClose,
    saveLoad,
    idSurvey,
    idQuestion,
    type,
}: Props) => {
    const { answer }: any = MasterMessage.surveys;
    const [rows, setRows] = useState<any>();
    const [row, setRow] = useState<any>();
    const [loading, setLoading] = useState<boolean>(true);
    const [save, setSave] = useState<any>(false);
    const [deleteAnswer, setDeleteAnswer] = useState<any>(false);
    const [message, setMessage] = useState<any>(false);
    const [messageEdit, setMessageEdit] = useState<any>(false);
    const [idAnswer, setIdAnswer] = useState<any>('');
    const [openAdd, setOpenAdd] = useState<boolean>(false);

    const formik = useFormik({
        initialValues: {
            label: '',
            description: '',
            status: true,
            question: idQuestion,
            survey: idSurvey,
        },
        validationSchema: Yup.object({
            label: Yup.string().when({
                is: () => Boolean(type === 'linear-label'),
                then: (schema) => schema.required('La etiqueta es obligatoria'),
            }),
            description: Yup.string().required('La descripción es obligatoria'),
        }),
        onSubmit: async (formModalAnswer) => {
            if (!row) {
                const body: any = await asyncPostAnswer(
                    formModalAnswer
                ).finally(() => setSave(true));
                if (body?.items !== undefined) {
                    setMessage(true);
                    saveLoad(true);
                } else if (body?.errors !== undefined) {
                    setMessage(false);
                }
            } else {
                asyncPatchAnswer(row?.id, formModalAnswer)
                    .then((body: any) => {
                        if (body?.items !== undefined) {
                            setMessageEdit(true);
                            saveLoad(true);
                        } else if (body?.errors !== undefined) {
                            setMessageEdit(false);
                        }
                    })
                    .finally(() => setSave(true));
            }
        },
    });

    useEffect(() => {
        if (row) {
            formik.values.label = row.label;
            formik.values.description = row.description;
            formik.values.status = row.status;
        }

        if (!openAdd) {
            asyncGetAnswersByQuestion(idQuestion, 0, 10000, true)
                .then((body: any) => {
                    if (body.items !== undefined) {
                        setRows(body.items.items);
                    } else {
                        SwalCustomClass.fire({
                            text: answer[body.message],
                            icon: 'error',
                            iconColor: '#1d1d1d',
                        });
                    }
                })
                .finally(() => setLoading(false));
        }
    }, [idQuestion, openAdd, row]);

    const onOpenClickAnswer = () => {
        formik.values.description = '';
        formik.values.label = '';
        formik.values.status = true;
        window.setTimeout(() => {
            setOpenAdd(true);
        }, 500);
    };

    const onEditClickAnswer = (row: any) => {
        setLoading(true);
        setRow(row?.row);
        setOpenAdd(true);
        window.setTimeout(() => {
            setLoading(false);
        }, 500);
    };

    const onCloseClickAnswer = () => {
        setRow(undefined);
        setOpenAdd(false);
        setSave(false);
        setDeleteAnswer(false);
        formik.handleReset;
    };

    const onClickDeleteAnswer = (rowId: string) => {
        setIdAnswer(rowId);
        setDeleteAnswer(true);
        setOpenAdd(true);
    };

    const onClickCancelDelete = () => {
        setIdAnswer('');
        setDeleteAnswer(false);
        setOpenAdd(false);
        formik.handleReset;
    };

    const onClickDelete = () => {
        asyncDeleteAnswer(idAnswer, idQuestion, idSurvey).finally(() => {
            asyncGetAnswersByQuestion(idQuestion, 0, 10000)
                .then((body: any) => {
                    if (body.items !== undefined) {
                        setRows(body.items.items);
                    }
                })
                .finally(() => {
                    window.setTimeout(() => {
                        saveLoad(true);
                    }, 800);
                });
        });
        setIdAnswer('');
        setDeleteAnswer(false);
        setOpenAdd(false);
        formik.handleReset;
    };

    const onChangeItems = (items: any[]) => {
        setRows(items);
        if (idQuestion)
            asyncUpdatePositionsAnwers(
                idQuestion,
                items.map((item: any) => item.id)
            );
    };

    const columns = [
        {
            field: 'label',
            headerName: 'Etiqueta',
            minWidth: 100,
            flex: 2,
        },
        {
            field: 'description',
            headerName: 'Descripción',
            minWidth: 100,
            flex: 2,
        },
        {
            field: 'status',
            headerName: 'Estado',
            minWidth: 100,
            flex: 1,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row: any) => {
                if (row.row.status === true) {
                    return (
                        <Chip
                            label="ACTIVO"
                            sx={{ ml: 1, fontSize: 11.5 }}
                            color="success"
                        />
                    );
                } else {
                    return (
                        <Chip
                            label="INACTIVO"
                            sx={{ ml: 1, fontSize: 11.5 }}
                            color="error"
                        />
                    );
                }
            },
        },
        {
            field: 'actions',
            headerName: 'Acciones',
            width: 100,
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row: any) => (
                <>
                    <Tooltip title="Editar">
                        <IconButton
                            size="small"
                            onClick={() => onEditClickAnswer(row)}
                        >
                            <EditIcon type="submit" color="primary" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Deshabilitar">
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => onClickDeleteAnswer(row.id)}
                                disabled={!row.row.status}
                            >
                                <BlockIcon
                                    type="submit"
                                    color={
                                        !row.row.status ? 'disabled' : 'primary'
                                    }
                                />
                            </IconButton>
                        </span>
                    </Tooltip>
                </>
            ),
        },
    ];

    return (
        <Modal
            open={open}
            onClose={() => onClose(false)}
            closeOnOverlayClick={false}
            styles={{
                modal: {
                    minWidth: !openAdd ? '90%' : '40%',
                    maxWidth: !openAdd ? '500px' : '',
                },
            }}
            classNames={{ root: !openAdd ? 'modal-case-mr' : '' }}
        >
            <Box>
                <Typography
                    color="primary"
                    component="h3"
                    sx={{
                        fontSize: { lg: 27, xs: 23 },
                        mt: { xs: 1.5, sm: 1.5, md: 1.5, lg: 0 },
                    }}
                >
                    {deleteAnswer
                        ? answer.titleDelete
                        : row
                            ? answer.titleEdit
                            : answer.titleNew}
                </Typography>
                <hr />
                <Box mt={!openAdd ? 1 : 3} px={1}>
                    <Grid
                        container
                        spacing={{ xs: 1, md: 1 }}
                        columnSpacing={{ xs: 1.5, sm: 1.5, md: 1.5 }}
                    >
                        {!openAdd ? (
                            <Grid item xs={12}>
                                <DraggableListView
                                    columns={
                                        type === 'linear-label'
                                            ? columns
                                            : columns.slice(1)
                                    }
                                    rows={rows}
                                    onChangeItems={onChangeItems}
                                />
                            </Grid>
                        ) : loading ? (
                            <Loading />
                        ) : (
                            <Grid item xs={12} mt={1}>
                                {save ? (
                                    <Typography align="center">
                                        {!row
                                            ? message
                                                ? answer.messageNew
                                                : answer.messageNewError
                                            : messageEdit
                                                ? answer.messageEdit
                                                : answer.messageEditError}
                                    </Typography>
                                ) : deleteAnswer ? (
                                    <Typography mt={1} align="center">
                                        {answer.messageInactive}
                                    </Typography>
                                ) : (
                                    <>
                                        {type === 'linear-label' && (
                                            <Grid item xs={12} mt={1}>
                                                <FieldText
                                                    label="Etiqueta"
                                                    name="label"
                                                    size="small"
                                                    required={true}
                                                    touched={
                                                        formik.touched.label
                                                    }
                                                    errors={formik.errors.label}
                                                    value={formik.values.label}
                                                    onChange={
                                                        formik.handleChange
                                                    }
                                                />
                                            </Grid>
                                        )}
                                        <Grid item xs={12} mt={1}>
                                            <FieldText
                                                label="Descripción"
                                                name="description"
                                                size="small"
                                                required={true}
                                                touched={
                                                    formik.touched.description
                                                }
                                                errors={
                                                    formik.errors.description
                                                }
                                                value={
                                                    formik.values.description
                                                }
                                                onChange={formik.handleChange}
                                            />
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        )}
                        {!save ? (
                            <Grid
                                item
                                xs={12}
                                mt={deleteAnswer ? 2 : 1}
                                sx={{
                                    textAlign: deleteAnswer
                                        ? 'center'
                                        : 'right',
                                }}
                            >
                                <ButtonGeneral
                                    onClick={
                                        deleteAnswer
                                            ? () => onClickDelete()
                                            : !openAdd
                                                ? () => onOpenClickAnswer()
                                                : formik.handleSubmit
                                    }
                                    startIcon={
                                        deleteAnswer ? (
                                            <CheckCircleIcon color="secondary" />
                                        ) : !openAdd ? (
                                            <AddCircleIcon color="secondary" />
                                        ) : (
                                            <SaveIcon color="secondary" />
                                        )
                                    }
                                    title={
                                        deleteAnswer
                                            ? 'Si'
                                            : !openAdd
                                                ? 'Agregar Respuesta'
                                                : 'Guardar'
                                    }
                                    sx={{ mr: 1 }}
                                />
                                <ButtonGeneral
                                    onClick={() =>
                                        deleteAnswer
                                            ? onClickCancelDelete()
                                            : !openAdd
                                                ? onClose(false)
                                                : onCloseClickAnswer()
                                    }
                                    startIcon={<CancelIcon color="secondary" />}
                                    title={deleteAnswer ? 'No' : 'Cerrar'}
                                />
                            </Grid>
                        ) : (
                            <Grid
                                item
                                xs={12}
                                mt={1}
                                sx={{ textAlign: 'center' }}
                            >
                                <ButtonGeneral
                                    onClick={() => onCloseClickAnswer()}
                                    startIcon={
                                        message || messageEdit ? (
                                            <CheckCircleIcon color="secondary" />
                                        ) : (
                                            <CancelIcon color="secondary" />
                                        )
                                    }
                                    title="Aceptar"
                                />
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </Box>
        </Modal>
    );
};

export default ModalAnswers;
